import type { DocumentSearchSortCriterion } from '~/types'
import type { CollectionRequestPaginationParams, CollectionResponse, IncludedFieldDependentResponse, Response,
} from '~/types/api/v2'

import type { WorkspaceIdentifier } from '../types'

import { useFetchWithDefaults } from '../useFetchWithDefaults'
import { useApiV2FetchFactory } from '../useApiV2FetchFactory'

type DocumentData = App.Data.Document.DocumentData
type DocumentSearchPayload = App.Data.Payloads.Documents.Search.DocumentSearchPayload
type DuplicateDocumentPayload = App.Data.Payloads.Documents.DuplicateDocumentPayload

export const responseDataTypeName: DocumentData['_type'] = 'document'

export const url = '/v2/documents'
export const getUrlById = (documentId: string) => `${url}/${documentId}`

export type IncludableField =
  'attachments' |
  'attachments.file' |
  'folder' |
  'folder.own_permissions' |
  'owner' |
  'contract' |
  'contract.user' |
  'contract.signing_settings' |
  'contract.template' |
  'contract.user_template' |
  'contract.signatures' |
  'contract.permission' |
  'workpace' |
  'languages' |
  'document_types' |
  'document_types.translations' |
  'jurisdictions' |
  'parties.address' |
  'parties.external_references' |
  'string_values' |
  'string_values.property' |
  'numeric_values' |
  'numeric_values.property' |
  'boolean_values.property' |
  'date_values' |
  'date_values.property' |
  'boolean_values' |
  'external_references' |
  'tags' |
  'file' |
  'terms_and_conditions' |
  'retention' |
  'reminders' |
  'reminders.schedules' |
  'reminders.recipients' |
  'summaries'

export const defaultDocumentsFieldsToInclude = [
  'folder.own_permissions',
  'owner',
  'tags',
  'contract.template',
  'contract.user_template',
  'contract.signing_settings',
  'parties.address',
  'terms_and_conditions',
  'string_values',
  'numeric_values',
  'date_values',
  'boolean_values',
  'attachments.file',
  'document_types.translations',
  'languages',
  'file',
  'retention',
  'summaries',
] satisfies IncludableField[]

export type DocumentIdentifier = WorkspaceIdentifier & {
  documentId: string
}
export type DocumentMutationParams<TPayload> = {
  payload: TPayload
} & DocumentIdentifier
export interface SortParameters {
  sort?: DocumentSearchSortCriterion
}
export type ParametersGetAll = {
  filters?: DocumentSearchPayload['filters']
} & SortParameters & CollectionRequestPaginationParams

export type ParametersGetAllInfinite = {
  filters?: DocumentSearchPayload['filters']
} & SortParameters & { per_page?: number }

export const useFetchDocument = () => {
  const { post } = useFetchWithDefaults()

  type DynamicResponse<TFieldsToInclude extends string[]> = IncludedFieldDependentResponse<
    DocumentData,
    TFieldsToInclude,
    IncludableField
  >

  const {
    createGetById,
    createUpdate,
    createDrop,
  } = useApiV2FetchFactory<DocumentData, IncludableField>(url)

  const getAll = <TFieldsToInclude extends IncludableField[] = []>({
    params,
    workspaceId,
    fieldsToInclude,
  }: WorkspaceIdentifier & { params?: ParametersGetAll, fieldsToInclude?: TFieldsToInclude },
  ) => {
    const { filters, sort, ...paramsWithoutFiltersAndSort } = params || {}

    return post<CollectionResponse<DynamicResponse<TFieldsToInclude>>>(
      {
        url: `${url}/search`,
        workspaceId,
        fetchOptions: {
          params: {
            ...paramsWithoutFiltersAndSort,
            ...(fieldsToInclude?.length ? { include: fieldsToInclude } : {}),
          },
          body: {
            filters,
            sort,
          },
        },
      },
    )
  }

  const getById = createGetById()

  const update = createUpdate<App.Data.Payloads.Documents.UpdateDocumentPayload>()

  const upload = <TFieldsToInclude extends IncludableField[] = []>({
    workspaceId,
    payload,
    fieldsToInclude,
  }: WorkspaceIdentifier & { payload: object, fieldsToInclude?: TFieldsToInclude }) =>
    post<Response<DynamicResponse<TFieldsToInclude>[]>>({
      url,
      workspaceId,
      fetchOptions: {
        body: payload,
        ...(fieldsToInclude?.length ? { params: { include: fieldsToInclude } } : {}),
      },
    })

  const drop = createDrop()

  const duplicate = ({
    payload,
    documentId,
    workspaceId,
  }: DocumentMutationParams<DuplicateDocumentPayload>,
  ) => post<Response<DynamicResponse<['contract']>>>({
    url: `${getUrlById(documentId)}/duplicate`,
    workspaceId,
    fetchOptions: {
      params: {
        include: ['contract'],
      },
      body: payload,
    },
  })

  const sendViaEmail = ({
    payload,
    documentId,
    workspaceId,
  }: DocumentMutationParams<App.Data.Payloads.Documents.SendToEmailPayload>,
  ) => post<void>({
    url: `${getUrlById(documentId)}/share/email`,
    workspaceId,
    fetchOptions: {
      body: payload,
    },
  })

  const getByContractId = <TFieldsToInclude extends IncludableField[] = []>({
    contractId,
    workspaceId,
    fieldsToInclude,
  }: WorkspaceIdentifier & { contractId: string, fieldsToInclude?: TFieldsToInclude }) =>
    post<CollectionResponse<DynamicResponse<TFieldsToInclude>>>({
      url: `${url}/search`,
      workspaceId,
      fetchOptions: {
        params: {
          ...(fieldsToInclude?.length ? { include: fieldsToInclude } : {}),
        },
        body: {
          filters: [{
            clause: 'where_all',
            rules: [{
              entity: 'document.contract',
              conditions: [{
                field: 'id',
                operator: 'is_any_of',
                value: [contractId],
              }],
            }],
          }],
        },
      },
    }).then(({ data }) => {
      const ret = data?.[0]
      // Error out if the result array is empty to allow automatic retries.
      if (!ret) {
        throw new Error('Document not found for contract')
      }
      return ret
    })

  return {
    getAll,
    getById,
    getByContractId,
    update,
    upload,
    drop,
    duplicate,
    sendViaEmail,
  }
}
